/*
 * Thunderstorm is a full web app framework!
 *
 * Typescript & Express backend infrastructure that natively runs on firebase function
 * Typescript & React frontend infrastructure
 *
 * Copyright (C) 2020 Adam van der Kruk aka TacB0sS
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Created by tacb0ss on 28/07/2018.
 */
import * as React from 'react';
import { _clearTimeout, _keys, _setTimeout, EmptyObject, ImplementationMissingException, Logger, LogLevel, sortArray } from '@nu-art/ts-common';
import { Thunder } from './Thunder.js';
let instances = 0;
export class BaseComponent extends React.Component {
    static MinLogLevel = LogLevel.Info;
    logger;
    timeoutMap = {};
    mounted = false;
    constructor(props) {
        super(props);
        this.logger = new Logger(this.constructor.name + '-' + (++instances));
        this.logger.setMinLevel(BaseComponent.MinLogLevel);
        this.logVerbose('Creating..');
        this._constructor();
        const __render = this.render?.bind(this);
        if (!__render)
            throw new ImplementationMissingException('Every UI Component must have a render function. Did you forget to add a render function?');
        this.render = () => {
            this.logVerbose('Rendering', this.state);
            return __render();
        };
        const __componentDidMount = this.componentDidMount?.bind(this);
        this.componentDidMount = () => {
            // @ts-ignore
            Thunder.getInstance().addUIListener(this);
            this.mounted = true;
            __componentDidMount?.();
        };
        const __componentWillUnmount = this.componentWillUnmount?.bind(this);
        this.componentWillUnmount = () => {
            __componentWillUnmount?.();
            // @ts-ignore
            Thunder.getInstance().removeUIListener(this);
            this.mounted = false;
        };
        this._deriveStateFromProps.bind(this);
        const state = this._deriveStateFromProps(props, this.state);
        if (state)
            this.state = state;
    }
    _constructor() {
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        if (!this.shouldReDeriveState(nextProps))
            return;
        if (this.state) //skip the first time when the shared-components MUST update
            this.logDebug('Received new props, calling deriveStateFromProps', nextProps);
        const state = this._deriveStateFromProps(nextProps, { ...this.state });
        if (state)
            this.setState(state);
    }
    reDeriveState = (state) => {
        this.logVerbose('reDeriveState called..');
        this._deriveStateFromProps(this.props, { ...this.state, ...state });
    };
    debounce(handler, key, ms = 0) {
        this.logWarning('THIS IS LEGACY S***, NEED TO REMOVE');
        _clearTimeout(this.timeoutMap[key]);
        this.timeoutMap[key] = _setTimeout(handler, ms);
    }
    throttle(handler, key, ms = 0) {
        this.logWarning('THIS IS LEGACY S***, NEED TO REMOVE');
        if (this.timeoutMap[key])
            return;
        this.timeoutMap[key] = _setTimeout(() => {
            handler();
            delete this.timeoutMap[key];
        }, ms);
    }
    shouldReDeriveState(nextProps) {
        const _shouldRederive = () => {
            const propKeys = sortArray(_keys(this.props || EmptyObject));
            const nextPropsKeys = sortArray(_keys(nextProps || EmptyObject));
            if (propKeys.length !== nextPropsKeys.length)
                return true;
            this.logVerbose('CurrentPropKeys:', propKeys);
            this.logVerbose('CurrentProps:', this.props);
            this.logVerbose('NextPropKeys:', nextPropsKeys);
            this.logVerbose('NextProps:', nextProps);
            if (propKeys.some((key, i) => propKeys[i] !== nextPropsKeys[i] || this.props[propKeys[i]] !== nextProps[nextPropsKeys[i]]))
                return true;
            return false;
        };
        const willReDerive = _shouldRederive();
        this.logVerbose(`component will${!willReDerive ? ' NOT' : ''} re-derive State`);
        return willReDerive;
    }
    shouldComponentUpdate(nextProps, nextState, nextContext) {
        const _shouldRender = () => {
            const stateKeys = sortArray(_keys(this.state || EmptyObject));
            const nextStateKeys = sortArray(_keys(nextState || EmptyObject));
            // this.logDebug('States Same Instance:', this.state === nextState);
            if (stateKeys.length !== nextStateKeys.length)
                return true;
            if (stateKeys.some((key, i) => stateKeys[i] !== nextStateKeys[i] || this.state[stateKeys[i]] !== nextState[nextStateKeys[i]]))
                return true;
            return false;
        };
        // const willRender = super.shouldComponentUpdate?.(nextProps, nextState, nextContext) || true;
        const willRender = _shouldRender();
        this.logVerbose(`component will${!willRender ? ' NOT' : ''} render`);
        return willRender;
    }
    logVerbose(...toLog) {
        this.logImpl(LogLevel.Verbose, false, toLog);
    }
    logDebug(...toLog) {
        this.logImpl(LogLevel.Debug, false, toLog);
    }
    logInfo(...toLog) {
        this.logImpl(LogLevel.Info, false, toLog);
    }
    logWarning(...toLog) {
        this.logImpl(LogLevel.Warning, false, toLog);
    }
    logError(...toLog) {
        this.logImpl(LogLevel.Error, false, toLog);
    }
    log(level, bold, ...toLog) {
        this.logImpl(level, bold, toLog);
    }
    logImpl(level, bold, toLog) {
        this.logger.log(level, bold, toLog);
    }
    toString() {
        return this.constructor.name;
    }
}
//# sourceMappingURL=ComponentBase.js.map